import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import { createEmployee } from '../../api/employees';
import { getActiveTools } from '../../api/tools';

const EMPTY_FORM = {
  name: '',
  email: '',
  department: '',
  ai_tool_id: '',
  start_date: '',
  end_date: '',
  tool_identifier: '',
  notes: '',
  monthly_cost: '',
  paid_by: 'Bacancy',
};

const AddEmployeeModal = ({ isOpen, onClose, onSaved, departments }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [tools, setTools] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
      getActiveTools()
        .then((res) => setTools(res.data))
        .catch(() => toast.error('Could not load tools.'));
    }
  }, [isOpen]);

  // Auto-fill monthly_cost from selected tool's price
  useEffect(() => {
    if (form.ai_tool_id) {
      const tool = tools.find((t) => String(t.id) === String(form.ai_tool_id));
      if (tool) {
        setForm((prev) => ({ ...prev, monthly_cost: Number(tool.monthly_price).toFixed(2) }));
      }
    } else {
      setForm((prev) => ({ ...prev, monthly_cost: '' }));
    }
  }, [form.ai_tool_id, tools]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email.';
    if (!form.department) errs.department = 'Department is required.';
    if (!form.ai_tool_id) errs.ai_tool_id = 'Please select an AI tool.';
    if (!form.start_date) errs.start_date = 'Start date is required.';
    if (form.end_date && form.end_date < form.start_date) errs.end_date = 'End date must be after start date.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      await createEmployee({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        department: form.department,
        ai_tool_id: Number(form.ai_tool_id),
        start_date: form.start_date,
        end_date: form.end_date || undefined,
        tool_identifier: form.tool_identifier.trim() || undefined,
        notes: form.notes.trim() || undefined,
        monthly_cost: form.monthly_cost ? Number(form.monthly_cost) : undefined,
        paid_by: form.paid_by || 'Bacancy',
      });
      toast.success('Employee added and tool allocated.');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add employee.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Employee & Allocate Tool" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Section: Employee Info */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Employee Information</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name</label>
              <input className="input" placeholder="John Doe" value={form.name} onChange={(e) => set('name', e.target.value)} />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="label">Email Address</label>
              <input className="input" type="email" placeholder="john@company.com" value={form.email} onChange={(e) => set('email', e.target.value)} />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="label">Department</label>
              <select className="input" value={form.department} onChange={(e) => set('department', e.target.value)}>
                <option value="">Select department...</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              {errors.department && <p className="text-xs text-red-500 mt-1">{errors.department}</p>}
            </div>
          </div>
        </div>

        <hr className="border-gray-100" />

        {/* Section: Tool Allocation */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Tool Allocation</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">AI Tool</label>
              <select className="input" value={form.ai_tool_id} onChange={(e) => set('ai_tool_id', e.target.value)}>
                <option value="">Select AI tool...</option>
                {tools.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} — ${Number(t.monthly_price).toFixed(2)}/mo</option>
                ))}
              </select>
              {errors.ai_tool_id && <p className="text-xs text-red-500 mt-1">{errors.ai_tool_id}</p>}
            </div>
            <div>
              <label className="label">Start Date</label>
              <input className="input" type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
              {errors.start_date && <p className="text-xs text-red-500 mt-1">{errors.start_date}</p>}
            </div>
            <div>
              <label className="label">End Date <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="input" type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
              {errors.end_date && <p className="text-xs text-red-500 mt-1">{errors.end_date}</p>}
            </div>
            <div>
              <label className="label">Tool ID / Username <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="input" placeholder="e.g. GitHub username" value={form.tool_identifier} onChange={(e) => set('tool_identifier', e.target.value)} />
            </div>
            <div>
              <label className="label">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="input" placeholder="Any additional notes..." value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
            <div>
              <label className="label">Monthly Charge (USD) <span className="text-gray-400 font-normal">(from tool price)</span></label>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.monthly_cost}
                onChange={(e) => set('monthly_cost', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Paid By</label>
              <select className="input" value={form.paid_by} onChange={(e) => set('paid_by', e.target.value)}>
                <option value="Bacancy">Bacancy</option>
                <option value="Client">Client</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={loading}>
            {loading ? 'Saving...' : 'Add Employee'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddEmployeeModal;
